import React, { useRef, useState } from 'react';
import { Film, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_FILES = 6;
const ACCEPT = 'video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska,.mp4,.webm,.mov,.avi,.mkv,.m4v';

/**
 * Multi-video uploader for vehicle admin forms (Cloudinary).
 * assets: [{ url, public_id?, thumbnail_url? }]
 * onChange(nextAssets)
 */
export default function VehicleVideoUploader({ assets = [], onChange, token, disabled = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const list = Array.isArray(assets) ? assets : [];

  const setAssets = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    if (!token) {
      toast.error('Admin session required to upload videos');
      return;
    }

    const remaining = MAX_FILES - list.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} videos per vehicle`);
      return;
    }

    const batch = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.message(`Only ${remaining} more video(s) can be added`);
    }

    const formData = new FormData();
    batch.forEach((file) => formData.append('files', file));

    setUploading(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/upload/videos`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const uploaded = (data.uploads || [])
        .map((u) => ({
          url: u.url,
          public_id: u.public_id || null,
          thumbnail_url: u.thumbnail_url || null,
          format: u.format || null,
          duration: u.duration ?? null,
          bytes: u.bytes ?? null,
        }))
        .filter((a) => a.url);

      if (!uploaded.length) {
        toast.error('No videos were uploaded');
        return;
      }

      setAssets([...list, ...uploaded]);

      if (data.errors?.length) {
        toast.message(`${uploaded.length} uploaded; some files failed`);
      } else {
        toast.success(
          uploaded.length === 1
            ? 'Video uploaded'
            : `${uploaded.length} videos uploaded`
        );
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : detail?.message || 'Failed to upload videos';
      toast.error(message);
      console.error(error);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (index) => {
    setAssets(list.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <label className="block text-sm font-['Manrope'] font-medium text-white/80">
            Vehicle Videos
          </label>
          <p className="text-xs text-white/40 font-['Manrope'] mt-1">
            Optional. Upload up to {MAX_FILES} videos (MP4, WebM, MOV). Stored in Cloudinary.
          </p>
        </div>
        <button
          type="button"
          disabled={disabled || uploading || list.length >= MAX_FILES}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-500/40 text-blue-300 hover:bg-blue-500/30 transition-colors font-['Manrope'] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Film className="w-4 h-4" />
          )}
          {uploading ? 'Uploading…' : 'Upload videos'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {list.length === 0 ? (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="w-full border border-dashed border-white/15 bg-white/[0.03] hover:border-blue-500/40 hover:bg-blue-500/5 transition-colors py-8 px-4 text-center disabled:opacity-50"
        >
          <Film className="w-8 h-8 text-white/30 mx-auto mb-2" />
          <p className="text-sm text-white/50 font-['Manrope']">
            Click to upload vehicle videos
          </p>
        </button>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {list.map((asset, index) => (
            <div
              key={`${asset.url}-${index}`}
              className="relative group border border-white/10 bg-black/40 overflow-hidden"
            >
              <video
                src={asset.url}
                className="w-full h-36 object-cover bg-black"
                muted
                playsInline
                preload="metadata"
              />
              <div className="absolute inset-x-0 bottom-0 p-2 flex items-center justify-between bg-gradient-to-t from-black/90 to-transparent">
                <span className="text-[10px] text-white/70 font-['Manrope'] uppercase tracking-wider">
                  Video {index + 1}
                  {asset.duration != null
                    ? ` · ${Math.round(asset.duration)}s`
                    : ''}
                </span>
                <button
                  type="button"
                  title="Remove"
                  onClick={() => removeAt(index)}
                  className="p-1.5 bg-white/10 hover:bg-red-600 text-white"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <p className="text-xs text-white/40 font-['Manrope']">
          {list.length} video{list.length === 1 ? '' : 's'} selected
        </p>
      )}
    </div>
  );
}
